export interface BackoffOptions {
  /** Delay for attempt 0, before jitter. */
  baseMs: number;
  /** Upper bound on the un-jittered delay. */
  capMs: number;
}

/** Highest exponent applied, so `baseMs * 2 ** exponent` stays a finite number. */
const MAX_EXPONENT = 30;

/**
 * Equal-jitter exponential backoff: half the capped exponential delay, plus a uniform
 * random point across the other half. Attempt 0 is the first retry.
 */
export function backoffDelay(attempt: number, options: BackoffOptions, random: () => number = Math.random): number {
  const exponent = Math.min(Math.max(0, Math.floor(attempt)), MAX_EXPONENT);
  const ceiling = Math.min(options.capMs, options.baseMs * 2 ** exponent);
  const half = ceiling / 2;
  return Math.round(half + random() * half);
}

export interface RetryLoop {
  /** Replaces any pending attempt with one at the next backoff delay. */
  scheduleNext: (action: () => void, context?: string) => void;
  /** Cancels a pending attempt, if any. */
  cancel: () => void;
  /** Returns the loop to its first, shortest delay. */
  reset: () => void;
  /** Attempts scheduled since the last reset. */
  attemptCount: () => number;
}

/** A cancellable exponential-backoff retry loop, logging each scheduled attempt via `describe`. */
export function createRetryLoop(
  options: BackoffOptions,
  describe: (delayMs: number, attempt: number, context?: string) => string,
  log: (message: string) => void,
): RetryLoop {
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  return {
    scheduleNext(action, context) {
      cancel();
      const delay = backoffDelay(attempt, options);
      attempt += 1;
      log(describe(delay, attempt, context));
      timer = setTimeout(() => {
        timer = null;
        action();
      }, delay);
    },
    cancel,
    reset() {
      attempt = 0;
    },
    attemptCount() {
      return attempt;
    },
  };
}
